import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { 
  Cog, 
  Factory, 
  Wrench, 
  Package, 
  Lightbulb,
  CheckCircle,
  ArrowRight,
  MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';

const capabilities = [
  {
    icon: Cog,
    title: 'Precision Machining',
    description: 'Proses machining presisi untuk pembuatan dan perbaikan komponen industri.',
    details: [
      'CNC Turning & Milling',
      'Multi-axis Machining',
      'Tight Tolerance Parts',
      'Various Material Processing'
    ],
    image: 'https://images.unsplash.com/photo-1738162837335-3745e5d16c09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjbmMlMjBtYWNoaW5pbmclMjBpbmR1c3RyaWFsJTIwd29ya3Nob3AlMjBtZXRhbCUyMGZhYnJpY2F0aW9ufGVufDB8fHx8MTc3MzExMDU2M3ww&ixlib=rb-4.1.0&q=85'
  },
  {
    icon: Factory,
    title: 'Metal Fabrication',
    description: 'Pembuatan komponen metal fabricated untuk kebutuhan industri.',
    details: [
      'Custom Metal Parts',
      'Sheet Metal Fabrication',
      'Structural Components',
      'Welding & Assembly'
    ],
    image: 'https://images.unsplash.com/photo-1720036236727-009f5b6f5a20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxjbmMlMjBtYWNoaW5pbmclMjBpbmR1c3RyaWFsJTIwd29ya3Nob3AlMjBtZXRhbCUyMGZhYnJpY2F0aW9ufGVufDB8fHx8MTc3MzExMDU2M3ww&ixlib=rb-4.1.0&q=85'
  },
  {
    icon: Wrench,
    title: 'Repair & Maintenance',
    description: 'Perbaikan dan perawatan equipment industri seperti pump, gearbox, dan lainnya.',
    details: [
      'Pump Repair & Overhaul',
      'Gearbox Reconditioning',
      'Valve Maintenance',
      'Hydraulic System Repair'
    ],
    image: 'https://images.unsplash.com/photo-1759159091728-e2c87b9d9315?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxjbmMlMjBtYWNoaW5pbmclMjBpbmR1c3RyaWFsJTIwd29ya3Nob3AlMjBtZXRhbCUyMGZhYnJpY2F0aW9ufGVufDB8fHx8MTc3MzExMDU2M3ww&ixlib=rb-4.1.0&q=85'
  },
  {
    icon: Package,
    title: 'Industrial Supply',
    description: 'Penyediaan komponen mechanical dan electrical untuk industri.',
    details: [
      'Mechanical Components',
      'Electrical Parts',
      'Industrial Equipment',
      'Spare Parts Supply'
    ],
    image: 'https://images.unsplash.com/photo-1745448797901-2a4c9d9af1c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxjbmMlMjBtYWNoaW5pbmclMjBpbmR1c3RyaWFsJTIwd29ya3Nob3AlMjBtZXRhbCUyMGZhYnJpY2F0aW9ufGVufDB8fHx8MTc3MzExMDU2M3ww&ixlib=rb-4.1.0&q=85'
  },
  {
    icon: Lightbulb,
    title: 'Engineering Solutions',
    description: 'Solusi engineering komprehensif untuk mendukung kebutuhan industri Anda.',
    details: [
      'Technical Consulting',
      'Design & Engineering',
      'Problem Solving',
      'Project Management'
    ],
    image: 'https://images.unsplash.com/photo-1593812725955-6d89f01ded2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZW5naW5lZXIlMjBzYWZldHklMjBoZWxtZXQlMjB3b3JrZXJ8ZW58MHx8fHwxNzczMTEwNTc1fDA&ixlib=rb-4.1.0&q=85'
  }
];

const CapabilitiesPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="capabilities-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#001582] to-[#001ea7] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFC107] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#FFC107] rounded-full" />
              <span className="text-white text-sm font-semibold uppercase tracking-wider">Kapabilitas</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Layanan <span className="text-[#FFC107]">Engineering</span> Terpadu
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              Kami menyediakan solusi engineering komprehensif untuk mendukung kebutuhan 
              industri Anda dengan standar kualitas tinggi.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Capabilities List */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-20">
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative rounded-3xl overflow-hidden">
                    <img 
                      src={capability.image}
                      alt={capability.title}
                      className="w-full aspect-[4/3] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#001582]/40 to-transparent" />
                  </div>
                </div>

                {/* Content */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="w-16 h-16 bg-gradient-to-r from-[#001582] to-[#001ea7] rounded-2xl flex items-center justify-center mb-6">
                    <capability.icon className="w-8 h-8 text-[#FFC107]" />
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-4">
                    {capability.title}
                  </h2>
                  <p className="text-slate-600 text-lg leading-relaxed mb-6">
                    {capability.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {capability.details.map((detail) => (
                      <li key={detail} className="flex items-center gap-3 text-slate-700">
                        <CheckCircle className="w-5 h-5 text-[#FFC107] flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                  <a 
                    href="https://wa.me/6285210005588" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-gradient-to-r from-[#001582] to-[#001ea7] text-white hover:from-[#001ea7] hover:to-[#001582] font-semibold rounded-xl">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Konsultasi Gratis
                    </Button>
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#001582] to-[#001ea7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-6">
            Butuh layanan engineering untuk proyek Anda?
          </h3>
          <a 
            href="https://wa.me/6285210005588" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button 
              size="lg"
              className="bg-[#FFC107] text-[#001582] hover:bg-[#FFD54F] font-bold px-8 py-6 rounded-xl"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Hubungi Kami Sekarang
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CapabilitiesPage;
